import { Box, Grid, Link, Typography } from "@mui/material";
import React, { useContext } from "react";
import { Link as RouterLink } from "react-router-dom";

import { Header, LoginHeader } from "../../components/Header";
import UserContext from "../../context/User";
import useStyle from "../Privacy/styles";
import Seo from "../../components/Seo/Seo";

/* Delete account page: the Google Play "Delete account URL". Google wants a
   web page, reachable without the app, that says how to ask for an account to
   be deleted and what is deleted or kept. Matches the app's real behaviour
   (backend utils/accountErasure.js): 30 days to restore, then the customer's
   details are erased; orders are kept for the restaurants' records. Same
   layout and styles as the Support page. */
const SUPPORT_EMAIL = "submissions@rushmyfood.com";

function Section({ title, children, classes }) {
  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle2" component="h2" className={classes.boldText} sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function P({ children }) {
  return (
    <Typography variant="subtitle2" sx={{ lineHeight: 1.7, my: 1 }}>
      {children}
    </Typography>
  );
}

function Bullets({ items }) {
  return (
    <Box component="ul" sx={{ pl: 3, my: 1 }}>
      {items.map((item, i) => (
        <li key={i}>
          <Typography variant="subtitle2" sx={{ lineHeight: 1.7 }}>{item}</Typography>
        </li>
      ))}
    </Box>
  );
}

function DeleteAccount() {
  const classes = useStyle();
  const { isLoggedIn } = useContext(UserContext);

  return (
    <Grid className={classes.root}>
      <Seo
        title="Delete your account"
        description="How to delete your RushMyFood account, from the app or by email, and what happens to your information."
        path="/delete-account"
      />
      {isLoggedIn ? <Header /> : <LoginHeader showIcon />}
      <Grid container className={classes.mainContainer}>
        <Box className={classes.imageContainer}>
          <Typography variant="h4" component="h1" color="textPrimary" align="center" className={classes.title}>
            Delete your account
          </Typography>
        </Box>
        <Grid container item xs={12} className={classes.mainContainer}>
          <Grid item xs={1} md={1} />
          <Grid container item xs={10} sm={10} md={9}>
            <Box>
              <P>
                You can delete your RushMyFood account at any time, from the RushMyFood app or by emailing us.
              </P>

              <Section title="In the RushMyFood app" classes={classes}>
                <Bullets
                  items={[
                    "Open the side menu and tap Profile.",
                    "Tap Delete my account at the bottom of the page and confirm.",
                  ]}
                />
              </Section>

              <Section title="Without the app" classes={classes}>
                <P>
                  Email{" "}
                  <Link href={`mailto:${SUPPORT_EMAIL}?subject=Delete%20my%20account`} underline="hover" sx={{ fontWeight: 600 }}>
                    {SUPPORT_EMAIL}
                  </Link>{" "}
                  from the email address on your account, with the subject "Delete my account". We'll delete it and
                  reply to confirm.
                </P>
              </Section>

              <Section title="What happens next" classes={classes}>
                <Bullets
                  items={[
                    "Your account is closed straight away and you're signed out on every device.",
                    "For 30 days you can change your mind: sign in again and we'll email you a code to restore it.",
                    "After 30 days we erase your name, email, phone number, saved addresses and saved cards. Your points, coupons and wallet balance are lost.",
                    "A Black Card membership stops renewing when you delete your account.",
                  ]}
                />
              </Section>

              <Section title="What we keep" classes={classes}>
                <P>
                  We keep records of orders you placed (the restaurant, items, amounts and dates) because restaurants
                  and RushMyFood need them for tax, accounting and legal reasons. After 30 days these records are no
                  longer linked to your name or contact details.
                </P>
              </Section>

              {/* Restaurant partner app (RushMyFood Restaurant): owners create staff
                  logins in the app, so Google wants deletion steps for those too.
                  The app itself only deactivates staff (Staff/StaffDetail.js). */}
              <Section title="Restaurant and staff accounts" classes={classes}>
                <P>These are the accounts used in the RushMyFood Restaurant app.</P>
                <Bullets
                  items={[
                    "To stop a staff login, the restaurant owner opens Staff in the RushMyFood Restaurant app, taps the staff member and taps Deactivate Staff Member. They can no longer sign in.",
                    <>
                      To delete a staff member's details or the whole restaurant account, the owner emails{" "}
                      <Link href={`mailto:${SUPPORT_EMAIL}?subject=Delete%20my%20restaurant%20account`} underline="hover" sx={{ fontWeight: 600 }}>
                        {SUPPORT_EMAIL}
                      </Link>{" "}
                      from the email address on the restaurant account, with the subject "Delete my restaurant account"
                      (or the staff member's name). We'll reply to confirm.
                    </>,
                    "We delete the logins and the names and contact details of the people on the account. We keep the restaurant's orders, sales, payouts and tax records, because the restaurant and RushMyFood need them for accounting, tax and legal reasons.",
                  ]}
                />
              </Section>

              <Box sx={{ mt: 4, mb: 6, display: "flex", gap: 3, flexWrap: "wrap" }}>
                <Link component={RouterLink} to="/privacy" underline="hover">Privacy Policy</Link>
                <Link component={RouterLink} to="/support" underline="hover">Support</Link>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default React.memo(DeleteAccount);
